// Builds the JSON the D&D Beyond importer extension (ddb-extension/) reads,
// from an item row as the API returns it to a DM.
//
// Pure and dependency-free so tests can import it under node. The server has
// already validated ddb_spec (shared/ddb.js), so this only parses and reshapes.

export function parseDdbSpec(raw) {
  if (!raw) return null
  if (typeof raw === 'object') return raw
  try {
    const spec = JSON.parse(raw)
    return spec && typeof spec === 'object' && spec.item ? spec : null
  } catch {
    return null
  }
}

// [[Target]] -> Target, [[Target|Label]] -> Label. Wiki-links only mean
// something on startyour.quest; on DDB they would be literal brackets.
// DDB's own tooltip tags ([spells]…[/spells]) are single-bracket and untouched.
export function stripWikiLinks(md) {
  return String(md ?? '').replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (_, target, label) =>
    (label ?? target).trim()
  )
}

const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

function inline(s) {
  return escapeHtml(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*(?!\s)(.+?)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/(^|\W)_(?!\s)(.+?)_(?=\W|$)/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2">$1</a>')
}

// The small markdown subset item descriptions use: paragraphs (single newlines
// become <br>, matching the site's pre-line rendering), headings, bullet and
// numbered lists, blockquotes, horizontal rules, bold, italic and links. DDB's
// description editor stores HTML, so anything fancier would not survive there
// anyway.
export function markdownToHtml(md) {
  const lines = String(md ?? '').replace(/\r\n?/g, '\n').split('\n')
  const out = []
  let para = []
  let list = null // { tag, items }
  let quote = []

  const flushPara = () => {
    if (para.length) out.push(`<p>${para.map(inline).join('<br>')}</p>`)
    para = []
  }
  const flushList = () => {
    if (list) out.push(`<${list.tag}>${list.items.map((i) => `<li>${inline(i)}</li>`).join('')}</${list.tag}>`)
    list = null
  }
  const flushQuote = () => {
    if (quote.length) out.push(`<blockquote><p>${quote.map(inline).join('<br>')}</p></blockquote>`)
    quote = []
  }
  const flushAll = () => {
    flushPara()
    flushList()
    flushQuote()
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    let m
    if (!line.trim()) {
      flushAll()
    } else if ((m = line.match(/^(#{1,6})\s+(.*)$/))) {
      flushAll()
      out.push(`<h${m[1].length}>${inline(m[2])}</h${m[1].length}>`)
    } else if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      flushAll()
      out.push('<hr>')
    } else if ((m = line.match(/^\s*[-*+]\s+(.*)$/)) || (m = line.match(/^\s*\d+[.)]\s+(.*)$/))) {
      const tag = /^\s*\d/.test(line) ? 'ol' : 'ul'
      flushPara()
      flushQuote()
      if (list && list.tag !== tag) flushList()
      if (!list) list = { tag, items: [] }
      list.items.push(m[1])
    } else if ((m = line.match(/^>\s?(.*)$/))) {
      flushPara()
      flushList()
      quote.push(m[1])
    } else {
      flushList()
      flushQuote()
      para.push(line.trim())
    }
  }
  flushAll()
  return out.join('')
}

// The importer's input. `name` always comes from the item itself, and the
// description falls back from the spec's own override to full_description to
// the card blurb, so a DM who never wrote DDB-specific text still gets one.
export function buildDdbPayload(item) {
  const spec = parseDdbSpec(item?.ddb_spec)
  if (!spec) return null
  const { description: override, ...mechanics } = spec.item
  const source = override || item.full_description || item.description || ''
  const payload = {
    item: { name: item.name, ...mechanics, description: markdownToHtml(stripWikiLinks(source)) },
  }
  if (spec.modifiers?.length) payload.modifiers = spec.modifiers
  if (spec.spells?.length) payload.spells = spec.spells
  if (spec.autoLink === false) payload.autoLink = false
  return payload
}

// One-line summary for the DM's detail view, e.g.
// "Rare · Wondrous Item · requires attunement · 5 charges (Dawn) · 1 spell".
export function summarizeDdbSpec(spec) {
  if (!spec?.item) return ''
  const { item } = spec
  const parts = [item.rarity]
  const base = item.baseType || 'Item'
  if (base === 'Weapon') parts.push(item.baseWeapon ? `Weapon (${item.baseWeapon})` : 'Weapon')
  else if (base === 'Armor') parts.push(item.baseArmor ? `Armor (${item.baseArmor})` : 'Armor')
  else if (item.magicItemType) parts.push(item.magicItemType)
  if (item.requiresAttunement) parts.push('requires attunement')
  if (item.hasCharges && item.charges) {
    parts.push(`${item.charges} charges${item.chargeReset ? ` (${item.chargeReset})` : ''}`)
  }
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`
  if (spec.modifiers?.length) parts.push(plural(spec.modifiers.length, 'modifier'))
  if (spec.spells?.length) parts.push(plural(spec.spells.length, 'spell'))
  return parts.filter(Boolean).join(' · ')
}
